import * as Sentry from "@sentry/react";
import { consoleDebug } from '../../util';

const roundToTwoDecimals = (num) => Math.round(num * 100) / 100;

const getPrice = (variant) => {
    const priceInCents = variant.specialPriceRec && variant.specialPriceRec < variant.priceRec
        ? variant.specialPriceRec
        : variant.priceRec;

    return roundToTwoDecimals(priceInCents / 100);
};

const formatCentsToDollars = (cents) => {
    if (cents === null || cents === undefined) return 0;
    return roundToTwoDecimals(cents / 100);
};

function deconstructAddress(addressString) {
    if (!addressString) {
        console.error('Empty address string provided');
        return null;
    }

    // Regular expression to match different parts of the address
    const addressRegex = /^(.*?),\s*(.*?),\s*(\w{2})\s*(\d{5}),\s*(\w+)$/;
    const match = addressString.match(addressRegex);

    if (match) {
        const [, street, city, state, zip, country] = match;

        return {
            street,
            city,
            state,
            zip,
            country: country === 'USA' ? 'US' : country
        };
    } else {
        console.error('Unable to parse address string:', addressString);
        return null;
    }
}

export function didSurfsideLoad(callback) {
    if (window.isCheckingSurfside) return;

    window.isCheckingSurfside = true;
    const intervalId = setInterval(() => {
        if (window.surf) {
            try {
                callback();
            } catch (error) {
                console.error("Error in Surfside callback:", error);
                Sentry.captureException(error);
            }
            clearInterval(intervalId);
            window.isCheckingSurfside = false;
        }
    }, 200);

    // Add a timeout to prevent infinite checking
    setTimeout(() => {
        if (window.isCheckingSurfside) {
            console.error("Surfside SDK failed to load within timeout period");
            clearInterval(intervalId);
            window.isCheckingSurfside = false;
        }
    }, 5000);
}

export function trackSurfsideEvent(type, product, selectedVariant, quantity = null, specialData = null, retailerId, retailerData) {
    if (!window.surf) {
        console.error('Surfside SDK is not initialized.');
        return false;
    }

    try {
        // Add Location Context
        let addressComponents;
        if (retailerData && retailerId && type == 'add') {
            addressComponents = deconstructAddress(retailerData.address);

            if (addressComponents) {
                const currentLocation = {
                    id: retailerId,
                    street: addressComponents.street,
                    city: addressComponents.city,
                    state_label: addressComponents.state,
                    zip: addressComponents.zip,
                    country_code: addressComponents.country,
                };

                window.surf('setLocation', currentLocation);
            }
        }

        // Determine if price is already in dollars or cents
        // Most prices over 100 are likely in cents
        const priceIsInCents = selectedVariant.priceRec > 200 || (selectedVariant.specialPriceRec && selectedVariant.specialPriceRec > 200);

        // Calculate price correctly based on determination
        let finalPrice;
        if (selectedVariant.specialPriceRec && selectedVariant.specialPriceRec < selectedVariant.priceRec) {
            finalPrice = priceIsInCents ? formatCentsToDollars(selectedVariant.specialPriceRec) : selectedVariant.specialPriceRec;
        } else {
            finalPrice = priceIsInCents ? formatCentsToDollars(selectedVariant.priceRec) : selectedVariant.priceRec;
        }

        // Calculate discount
        const regularPrice = priceIsInCents ? formatCentsToDollars(selectedVariant.priceRec) : selectedVariant.priceRec;
        const discount = selectedVariant.specialPriceRec ? roundToTwoDecimals(regularPrice - finalPrice) : 0;

        const surfsideData = {
            id: product.id,
            name: product.name,
            brand: product.brand?.name,
            category: product.category,
            category2: product.subcategory,
            variant: selectedVariant.option,
            price: finalPrice,
            quantity: quantity || 1,
            discount: discount,
            currency: 'USD'
        };

        if (surfsideData) {
            consoleDebug('🌊 Tracking Surfside Event:', {
                type: type,
                data: surfsideData,
            });
        }

        if (specialData) {
            surfsideData.coupon = specialData;
        }

        window.surf('addProduct:range', surfsideData);

        switch (type) {
            case 'add':
                window.surf('setCommerceAction:range', 'add');
                break;
            case 'remove':
                window.surf('setCommerceAction:range', 'remove');
                break;
            case 'detail':
                window.surf('setCommerceAction:range', 'detail');
                break;
            case 'checkout':
                window.surf('setCommerceAction:range', 'checkout');
                break;
            default:
                // No action needed for purchase event
                break;
        }

        Sentry.captureEvent({
            message: `Product ${type === 'add' ? 'added to' : type === 'remove' ? 'removed from' : type === 'checkout' ? 'sent to checkout' : 'viewed in'} cart`,
            level: 'info',
            tags: surfsideData
        });

        return true;
    } catch (error) {
        console.error('Error tracking Surfside event:', error);
        Sentry.captureException(error);
        return false;
    }
}

export function trackSurfsideProductsLoop(checkoutData, type, specialsData = {}, retailerId = null, retailerData = null) {
    if (!window.surf) {
        console.error('Surfside SDK is not initialized.');
        return false;
    }

    try {
        let products;

        if (!checkoutData.items || !Array.isArray(checkoutData.items) || checkoutData.items.length === 0) {
            console.error('No products to send to Surfside checkout');
            return false;
        }

        if (checkoutData.priceSummary) {
            // Create a formatted version for display in logs
            const formattedPriceSummary = {
                discounts: formatCentsToDollars(checkoutData.priceSummary.discounts),
                fees: formatCentsToDollars(checkoutData.priceSummary.fees),
                mixAndMatch: formatCentsToDollars(checkoutData.priceSummary.mixAndMatch),
                rewards: formatCentsToDollars(checkoutData.priceSummary.rewards),
                subtotal: formatCentsToDollars(checkoutData.priceSummary.subtotal),
                taxes: formatCentsToDollars(checkoutData.priceSummary.taxes),
                total: formatCentsToDollars(checkoutData.priceSummary.total)
            };

            // Optional: replace the original price summary with formatted values
            // This ensures all price data sent to Surfside is in dollars
            checkoutData.priceSummary = formattedPriceSummary;
        }

        products = checkoutData.items;

        if (products) {
            consoleDebug('🌊 Surfside Products Loop Data:', products);
        }

        // Add location data if available
        if (retailerId && retailerData && retailerData.address) {
            const addressComponents = deconstructAddress(retailerData.address);

            if (addressComponents) {
                const currentLocation = {
                    id: retailerId,
                    street: addressComponents.street,
                    city: addressComponents.city,
                    state_label: addressComponents.state,
                    zip: addressComponents.zip,
                    country_code: addressComponents.country,
                };

                window.surf('setLocation', currentLocation);
            }
        }

        // Track each product
        let successCount = 0;
        products.forEach(product => {
            const productData = product.product;
            if (!productData || !productData.variants) {
                console.error('Invalid product data format:', product);
                return;
            }

            const selectedVariant = productData.variants.find(variant => variant.option === product.option);
            if (!selectedVariant) {
                console.error('Selected variant not found for product:', productData.id);
                return;
            }

            const specialData = specialsData?.[productData.id];

            const success = trackSurfsideEvent(type, productData, selectedVariant, product.quantity, specialData, retailerId, retailerData);
            if (success) successCount++;
        });

        // Only set commerce action if products were successfully added
        if (successCount > 0) {
            window.surf('setCommerceAction:range', type === 'checkout' ? 'checkout' : type);
            console.log(`Successfully tracked ${successCount}/${products.length} products for Surfside ${type} event`);
            return true;
        } else {
            console.error(`Failed to track any products for Surfside ${type} event`);
            return false;
        }
    } catch (error) {
        console.error(`Error in trackSurfsideProductsLoop for ${type}:`, error);
        Sentry.captureException(error);
        return false;
    }
}

export function trackSurfsidePurchase(orderNumber, order, retailerId, retailerData) {
    if (!window.surf) {
        console.error('Surfside SDK is not initialized.');
        return false;
    }

    try {
        console.log('Tracking Surfside purchase:', { orderNumber, orderTotal: order.total, itemCount: order.items?.length });

        // Add Location Context
        if (retailerData && retailerId) {
            const addressComponents = deconstructAddress(retailerData.address);

            if (addressComponents) {
                const currentLocation = {
                    id: retailerId,
                    street: addressComponents.street,
                    city: addressComponents.city,
                    state_label: addressComponents.state,
                    zip: addressComponents.zip,
                    country_code: addressComponents.country,
                };

                window.surf('setLocation', currentLocation);
            }
        }

        const checkoutData = {
            id: orderNumber,
            revenue: formatCentsToDollars(order.total),
            tax: formatCentsToDollars(order.tax || 0),
            shipping: 0,
            currency: 'USD'
        };

        window.surf('addTransaction:range', checkoutData);

        Sentry.captureEvent({
            message: 'Successful checkout transaction',
            level: 'info',
            tags: {
                ...checkoutData,
                customer_id: order?.customer?.id,
                order_type: order.pickup ? "pickup" : "delivery",
                payment_type: order.paymentMethod
            }
        });

        if (!order.items || !Array.isArray(order.items) || order.items.length === 0) {
            console.error('No products in order for Surfside purchase tracking');
            window.surf('setCommerceAction:range', 'purchase');
            return false;
        }

        let successCount = 0;
        order.items.forEach(product => {
            try {
                const productData = product.product;
                if (!productData || !productData.variants) {
                    console.error('Invalid product data format in order:', product);
                    return;
                }

                const selectedVariant = productData.variants.find(variant => variant.option === product.option);
                if (!selectedVariant) {
                    console.error('Selected variant not found for product in order:', productData.id);
                    return;
                }

                const price = getPrice(selectedVariant);
                const surfsideData = {
                    id: productData.id,
                    name: productData.name,
                    brand: productData.brand?.name,
                    category: productData.category,
                    category2: productData.subcategory,
                    variant: selectedVariant.option,
                    price: price,
                    quantity: product.quantity || 1,
                    discount: roundToTwoDecimals(selectedVariant.priceRec - price),
                    currency: 'USD'
                };

                window.surf('addProduct:range', surfsideData);
                successCount++;
            } catch (error) {
                console.error('Error adding product to Surfside purchase:', error);
            }
        });

        window.surf('setCommerceAction:range', 'purchase');
        return successCount > 0;
    } catch (error) {
        console.error('Error tracking Surfside purchase:', error);
        Sentry.captureException(error);
        return false;
    }
}

// Promise-based version of the didSurfsideLoad function
export function didSurfsideLoadPromise(timeout = 5000) {
    return new Promise((resolve, reject) => {
        if (window.surf) {
            // Surfside already loaded
            resolve(window.surf);
            return;
        }

        if (window.isCheckingSurfside) {
            // Another check is already in progress, set up our own timeout
            const timeoutId = setTimeout(() => {
                reject(new Error("Surfside SDK loading timeout while another check was in progress"));
            }, timeout);

            // Set up an interval to check if surf becomes available
            const intervalId = setInterval(() => {
                if (window.surf) {
                    clearInterval(intervalId);
                    clearTimeout(timeoutId);
                    resolve(window.surf);
                }
            }, 200);

            return;
        }

        // Start our own check
        window.isCheckingSurfside = true;

        const intervalId = setInterval(() => {
            if (window.surf) {
                clearInterval(intervalId);
                clearTimeout(timeoutId);
                window.isCheckingSurfside = false;
                resolve(window.surf);
            }
        }, 200);

        // Add a timeout to prevent infinite checking
        const timeoutId = setTimeout(() => {
            clearInterval(intervalId);
            window.isCheckingSurfside = false;
            reject(new Error("Surfside SDK failed to load within timeout period"));
        }, timeout);
    });
}

// Fixed Promise-based version of trackSurfsideProductsLoop
export function trackSurfsideProductsLoopPromise(checkoutData, type, specialsData = {}, retailerId = null, retailerData = null, timeout = 2000) {
    return new Promise((resolve) => {
        // First ensure Surfside is loaded
        didSurfsideLoadPromise(timeout)
            .then((surf) => {
                try {
                    if (!checkoutData.items || !Array.isArray(checkoutData.items) || checkoutData.items.length === 0) {
                        console.error('No products to send to Surfside checkout');
                        resolve(false);
                        return;
                    }

                    // Format price data if needed
                    if (checkoutData.priceSummary) {
                        const formattedPriceSummary = {
                            discounts: formatCentsToDollars(checkoutData.priceSummary.discounts),
                            fees: formatCentsToDollars(checkoutData.priceSummary.fees),
                            mixAndMatch: formatCentsToDollars(checkoutData.priceSummary.mixAndMatch),
                            rewards: formatCentsToDollars(checkoutData.priceSummary.rewards),
                            subtotal: formatCentsToDollars(checkoutData.priceSummary.subtotal),
                            taxes: formatCentsToDollars(checkoutData.priceSummary.taxes),
                            total: formatCentsToDollars(checkoutData.priceSummary.total)
                        };

                        checkoutData.priceSummary = formattedPriceSummary;
                    }

                    const products = checkoutData.items;
                    console.log('🌊 Surfside Products Loop Data:', products);

                    // Add location data if available
                    if (retailerId && retailerData && retailerData.address) {
                        const addressComponents = deconstructAddress(retailerData.address);

                        if (addressComponents) {
                            const currentLocation = {
                                id: retailerId,
                                street: addressComponents.street,
                                city: addressComponents.city,
                                state_label: addressComponents.state,
                                zip: addressComponents.zip,
                                country_code: addressComponents.country,
                            };

                            window.surf('setLocation', currentLocation);
                        }
                    }

                    // Add transaction data for checkout (if applicable)
                    if (type === 'checkout' && checkoutData.priceSummary) {
                        const transactionData = {
                            id: checkoutData.id || `cart-${Date.now()}`, // Cart ID or generated ID
                            revenue: checkoutData.priceSummary.total,
                            tax: checkoutData.priceSummary.taxes || 0,
                            shipping: 0, // Assuming no shipping cost
                            currency: 'USD'
                        };

                        // For checkout, we use addTransaction instead of tracking each product separately
                        window.surf('addTransaction:range', transactionData);
                        console.log('🌊 Surfside Transaction Data:', transactionData);
                    }

                    // Track each product ONLY ONCE (not firing separate events)
                    let successCount = 0;
                    products.forEach(product => {
                        const productData = product.product;
                        if (!productData || !productData.variants) {
                            console.error('Invalid product data format:', product);
                            return;
                        }

                        const selectedVariant = productData.variants.find(variant => variant.option === product.option);
                        if (!selectedVariant) {
                            console.error('Selected variant not found for product:', productData.id);
                            return;
                        }

                        // Get special data for this product
                        const specialData = specialsData?.[productData.id];

                        // Determine if price is already in dollars or cents
                        const priceIsInCents = selectedVariant.priceRec > 200 ||
                            (selectedVariant.specialPriceRec && selectedVariant.specialPriceRec > 200);

                        // Calculate price correctly based on determination
                        let finalPrice;
                        if (selectedVariant.specialPriceRec && selectedVariant.specialPriceRec < selectedVariant.priceRec) {
                            finalPrice = priceIsInCents ? formatCentsToDollars(selectedVariant.specialPriceRec) : selectedVariant.specialPriceRec;
                        } else {
                            finalPrice = priceIsInCents ? formatCentsToDollars(selectedVariant.priceRec) : selectedVariant.priceRec;
                        }

                        // Calculate discount
                        const regularPrice = priceIsInCents ? formatCentsToDollars(selectedVariant.priceRec) : selectedVariant.priceRec;
                        const discount = selectedVariant.specialPriceRec ? roundToTwoDecimals(regularPrice - finalPrice) : 0;

                        // Create the product data
                        const surfsideData = {
                            id: productData.id,
                            name: productData.name,
                            brand: productData.brand?.name,
                            category: productData.category,
                            category2: productData.subcategory,
                            variant: selectedVariant.option,
                            price: finalPrice,
                            quantity: product.quantity || 1,
                            discount: discount,
                            currency: 'USD'
                        };

                        if (specialData) {
                            surfsideData.coupon = specialData;
                        }

                        // Add the product data without firing an event yet
                        window.surf('addProduct:range', surfsideData);
                        console.log(`🌊 Added product to Surfside event: ${productData.name}`);
                        successCount++;
                    });

                    // Only set the commerce action ONCE after all products have been added
                    if (successCount > 0) {
                        window.surf('setCommerceAction:range', type === 'checkout' ? 'checkout' : type);
                        console.log(`🌊 Successfully tracked ${successCount} products with a single Surfside ${type} event`);
                        resolve(true);
                    } else {
                        console.error(`🌊 Failed to track any products for Surfside ${type} event`);
                        resolve(false);
                    }
                } catch (error) {
                    console.error(`Error in trackSurfsideProductsLoopPromise for ${type}:`, error);
                    Sentry.captureException(error);
                    resolve(false);
                }
            })
            .catch((error) => {
                console.error('Surfside SDK loading failed:', error);
                resolve(false);
            });
    });
}
